import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import apiClient from "@/lib/apiClient";
import { toast } from "sonner";
import { Helmet } from 'react-helmet-async';
import { Lock, AlertCircle, ArrowLeft, KeyRound } from "lucide-react";

const NewPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    if (!token) {
      setError("無効なリセットリンクです。");
      return;
    }

    if (password.length < 6) {
      setError("パスワードは6文字以上で入力してください。");
      return;
    }

    if (password !== confirmPassword) {
      setError("パスワードが一致しません。");
      return;
    }

    try {
      await apiClient.patch(`/customer/resets_password/${token}`, { password });
      toast.success("パスワードが変更されました。ログインしてください。");
      navigate("/login");
    } catch (err: any) {
      setError(err.response?.data?.error || "パスワードのリセットに失敗しました。");
    }
  };

  return (
    <>
      <Helmet>
        <title>新しいパスワードを設定</title>
        <meta property="og:title" content="新しいパスワードを設定" />
      </Helmet>

      <div className="min-h-screen bg-theme-bg flex flex-col">
        <div className="flex-1 flex items-center justify-center py-12 px-4 pt-24">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-theme-primary border-theme rounded-theme-lg flex items-center justify-center mx-auto mb-4 shadow-theme">
                <KeyRound className="w-8 h-8 text-theme-button" />
              </div>
              <h2 className="text-3xl [font-weight:var(--theme-font-weight)] text-theme-bg-text">
                新しいパスワードを設定
              </h2>
              <p className="mt-2 [font-weight:var(--theme-font-weight-button)] text-theme-bg-text opacity-70">
                新しいパスワードを入力してください
              </p>
            </div>

            <div className="bg-white border-theme rounded-theme-lg shadow-theme p-6 sm:p-8">
              {!token && (
                <div className="mb-6 p-4 bg-red-100 border-2 border-red-400 rounded-theme">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-red-400 border-theme rounded-theme flex items-center justify-center flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-black" />
                    </div>
                    <div>
                      <h3 className="[font-weight:var(--theme-font-weight)] text-black">無効なリンク</h3>
                      <p className="mt-1 [font-weight:var(--theme-font-weight-button)] text-red-700 text-sm">
                        このリンクは無効または期限切れです。パスワード再設定画面から再度お試しください。
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 bg-red-100 border-2 border-red-400 rounded-theme">
                    <p className="[font-weight:var(--theme-font-weight-button)] text-red-700">{error}</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="password" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <Lock className="h-4 w-4" />
                    新しいパスワード
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="6文字以上で入力"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                  />
                  <p className="mt-2 text-xs [font-weight:var(--theme-font-weight-button)] text-gray-500">
                    6文字以上で入力してください
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="flex items-center gap-2 text-sm [font-weight:var(--theme-font-weight-button)] text-black mb-2">
                    <Lock className="h-4 w-4" />
                    パスワード（確認）
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="もう一度入力"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="border-theme rounded-theme focus:ring-2 focus:ring-theme-primary focus:border-[var(--theme-border-color)]"
                  />
                </div>

                <button
                  type="submit"
                  disabled={!token}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-theme-primary border-theme rounded-theme [font-weight:var(--theme-font-weight)] text-theme-button shadow-theme hover:shadow-theme-hover hover:translate-x-[2px] hover:translate-y-[2px] active:shadow-none active:translate-x-[4px] active:translate-y-[4px] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <KeyRound className="w-4 h-4" />
                  パスワードを変更
                </button>
              </form>

              <div className="mt-6 text-center">
                <a
                  href="/login"
                  className="inline-flex items-center gap-2 [font-weight:var(--theme-font-weight-button)] text-theme-primary hover:opacity-80"
                >
                  <ArrowLeft className="w-4 h-4" />
                  ログイン画面に戻る
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default NewPasswordPage;
